import React from "react";
import { LockOutlined } from "@ant-design/icons";
import { dataStreamLabel, VIEWABLE_DATA_STREAMS } from "@utils/data_access";
import type { DataStream } from "@utils/data_access";

/** Shown in place of the metric list for a stream the user cannot read. */
export const NO_PERMISSION_LABEL = "You don't have permission to view this data";

/** Tooltip copy for a locked group heading. */
export const NO_PERMISSION_TOOLTIP = NO_PERMISSION_LABEL;

/** A group heading with a trailing padlock. */
export const LockedGroupLabel = ({ label }: { label: React.ReactNode }) => (
  <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
    {label}
    <LockOutlined style={{ opacity: 0.7 }} />
  </span>
);

// The metric dropdowns have no helmet group yet, so a locked helmet group would be the only helmet entry.
const DROPDOWN_STREAMS = VIEWABLE_DATA_STREAMS.filter((stream) => stream !== "helmet");

/**
 * Re-inserts stream groups the backend dropped from a dropdown payload.
 *
 * `DropDownViews` omits a denied metric type from its response rather than
 * answering 403, so a locked stream would otherwise vanish from the menu with no
 * explanation. This puts the heading back with a single unclickable row under it,
 * so the user can see the data exists and knows to ask for access.
 *
 * Only streams the team actually has data for are re-added — a stream the team
 * never collected stays absent, exactly as before.
 */
export const withLockedStreamGroups = (
  items: any[] | undefined,
  canAccess: (stream: DataStream) => boolean,
  allMetricTypes: Record<string, any[]> | undefined,
  streams: DataStream[] = DROPDOWN_STREAMS
): any[] => {
  const existing = items ?? [];
  const present = new Set(existing.map((item) => item?.key));

  const locked = streams
    .filter(
      (stream) =>
        !canAccess(stream) &&
        !present.has(stream) &&
        (allMetricTypes?.[stream]?.length ?? 0) > 0
    )
    .map((stream) => ({
      key: stream,
      label: <LockedGroupLabel label={dataStreamLabel(stream)} />,
      children: [
        {
          key: `${stream}__no-permission`,
          label: NO_PERMISSION_LABEL,
          disabled: true,
        },
      ],
    }));

  return locked.length > 0 ? [...existing, ...locked] : existing;
};
